import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { ArrowLeft } from 'lucide-react';

import { ProfileFormField } from '@/components/shared/ProfileFormField';
import {
  getNutritionistInvoiceSettings,
  getNutritionistProfile,
  updateStoredUserProfile,
  updateNutritionistInvoiceProfile,
  updateNutritionistInvoiceSettings,
  type InvoiceSettingsPayload,
} from '@/lib/apiService';
import type { CurrencyOption, Nutritionist } from '@/types/auth';
import { DEFAULT_COUNTRY_CURRENCY, getCountryDisplayName, getCurrencyForCountry } from '@/utils/countryCurrency';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';

interface InvoiceTaxFormState {
  currency: string;
  paymentDue: string;
  practiceNameOnInvoice: string;
  showLogoOnInvoice: boolean;
  reminderBeforeDueEnabled: boolean;
  reminderBeforeDue: string;
  overdueReminderEnabled: boolean;
  vatRate: string;
}

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD' },
  { value: 'GBP', label: 'GBP' },
  { value: 'EUR', label: 'EUR' },
  { value: 'INR', label: 'INR' },
];

const LEGACY_CURRENCY_CODES: Record<string, string> = {
  DOLLAR: 'USD',
  DOLLARS: 'USD',
  POUND: 'GBP',
  POUNDS: 'GBP',
  RUPEE: 'INR',
  RUPEES: 'INR',
};

const DEFAULT_PAYMENT_DUE_DAYS = 7;
const DEFAULT_PAYMENT_DUE_VALUE = String(DEFAULT_PAYMENT_DUE_DAYS);

const PAYMENT_DUE_OPTIONS = [
  { value: DEFAULT_PAYMENT_DUE_VALUE, label: '7 Days' },
  { value: '14', label: '14 Days' },
  { value: '30', label: '30 Days' },
  { value: '45', label: '45 Days' },
];

const PAYMENT_DUE_VALUES = new Set(PAYMENT_DUE_OPTIONS.map((option) => option.value));

const REMINDER_BEFORE_OPTIONS = [
  { value: '1', label: '1 Day Before' },
  { value: '2', label: '2 Days Before' },
  { value: '3', label: '3 Days Before' },
  { value: '5', label: '5 Days Before' },
];

const toggleSwitchSx: SxProps<Theme> = {
  width: 42,
  height: 24,
  p: 0,
  '& .MuiSwitch-switchBase': {
    p: 0,
    m: '2px',
    color: '#fff',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        bgcolor: '#13B8A6',
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 20,
    height: 20,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.22)',
    border: 'none',
    bgcolor: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: '999px',
    bgcolor: '#DDE3E8',
    opacity: 1,
  },
};

const compactSelectSx: SxProps<Theme> = {
  width: { xs: '100%', sm: 150 },
  '& .MuiOutlinedInput-root': {
    height: 42,
    borderRadius: '10px',
    bgcolor: '#fff',
    '& fieldset': {
      borderColor: '#D4DEE7',
    },
    '&:hover fieldset': {
      borderColor: '#BCCBD7',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#52B2D7',
    },
  },
  '& .MuiSelect-select': {
    py: 0.9,
    fontSize: '0.88rem',
    color: '#35424D',
  },
};

const invoiceFieldSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    minHeight: 56,
    borderRadius: '12px',
    '& input': {
      paddingTop: '10px',
      paddingBottom: '14px',
      paddingLeft: '16px',
      paddingRight: '16px',
      fontSize: '0.94rem',
    },
    '& .MuiSelect-select': {
      paddingTop: '10px',
      paddingBottom: '14px',
      paddingLeft: '16px',
      paddingRight: '16px',
      fontSize: '0.94rem',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.83rem',
  },
  '& .MuiInputLabel-shrink': {
    transform: 'translate(14px, -10px) scale(0.75)',
  },
};

const lockedCurrencyFieldSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root.Mui-disabled': {
    bgcolor: '#F8FAFC',
  },
  '& .MuiSelect-select.Mui-disabled': {
    WebkitTextFillColor: '#1A2530',
  },
  '& .MuiFormHelperText-root.Mui-disabled': {
    color: '#687887',
  },
};

interface SettingsCardProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}

const SettingsCard: React.FC<SettingsCardProps> = ({ title, subtitle, children }) => (
  <Box
    sx={{
      borderRadius: '14px',
      border: '1px solid transparent',
      borderBottomColor: '#e4ebf1',
      bgcolor: '#fff',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
      px: { xs: 2, sm: 3 },
      py: { xs: 2.7, sm: 3.2 },
    }}
  >
    <Typography sx={{ color: '#1F2B34', fontWeight: 700, fontSize: '1.05rem' }}>{title}</Typography>
    <Typography sx={{ mt: 0.85, color: '#6A7784', fontSize: '0.84rem', lineHeight: 1.35 }}>{subtitle}</Typography>
    <Box sx={{ mt: 2.8 }}>{children}</Box>
  </Box>
);

const parseIntegerInput = (rawValue: string, fallback: number): number => {
  const parsed = Number.parseInt(rawValue, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const readNumber = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizePaymentDueValue = (value: unknown): string => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_PAYMENT_DUE_VALUE;

  const normalizedValue = String(Math.trunc(parsed));
  return PAYMENT_DUE_VALUES.has(normalizedValue) ? normalizedValue : DEFAULT_PAYMENT_DUE_VALUE;
};

const normalizeCurrencyCode = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const code = value.trim().toUpperCase();
  return LEGACY_CURRENCY_CODES[code] || code;
};

const unwrapProfile = (response: unknown): Partial<Nutritionist> => {
  if (response && typeof response === 'object' && 'data' in response) {
    const data = (response as { data?: unknown }).data;
    if (data && typeof data === 'object') return data as Partial<Nutritionist>;
  }
  return response && typeof response === 'object' ? response as Partial<Nutritionist> : {};
};

const normalizeCurrencyOptions = (profile: Partial<Nutritionist>): Array<{ value: string; label: string }> => {
  const source = Array.isArray(profile.availableCurrencies) ? profile.availableCurrencies : [];
  const options = source
    .map((currency): { value: string; label: string } | null => {
      if (typeof currency === 'string') {
        const code = normalizeCurrencyCode(currency);
        return code ? { value: code, label: code } : null;
      }

      if (currency && typeof currency === 'object') {
        const option = currency as CurrencyOption;
        const code = normalizeCurrencyCode(String(option.code ?? option.value ?? ''));
        if (!code) return null;
        const labelParts = [
          option.label,
          option.name,
          option.symbol ? `${code} (${option.symbol})` : code,
        ];
        const label = labelParts.find((part) => typeof part === 'string' && part.trim()) || code;
        return { value: code, label: String(label).trim() };
      }

      return null;
    })
    .filter((option): option is { value: string; label: string } => Boolean(option));

  return options.length ? options : CURRENCY_OPTIONS;
};

const InvoiceTaxDetailsView: React.FC = () => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState<InvoiceTaxFormState>({
    currency: 'USD',
    paymentDue: DEFAULT_PAYMENT_DUE_VALUE,
    practiceNameOnInvoice: '',
    showLogoOnInvoice: true,
    reminderBeforeDueEnabled: true,
    reminderBeforeDue: '2',
    overdueReminderEnabled: true,
    vatRate: '20',
  });
  const [profileCountryLabel, setProfileCountryLabel] = useState('');
  const [isCurrencyDerivedFromCountry, setIsCurrencyDerivedFromCountry] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const updateField = <K extends keyof InvoiceTaxFormState,>(field: K, value: InvoiceTaxFormState[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    let isMounted = true;

    async function fetchInvoiceDetails() {
      setIsLoadingSettings(true);
      try {
        const [profileResponse, invoiceSettings] = await Promise.all([
          getNutritionistProfile(),
          getNutritionistInvoiceSettings(),
        ]);
        if (!isMounted) return;

        const profile = unwrapProfile(profileResponse);
        const nextCurrencyOptions = normalizeCurrencyOptions(profile);
        const profileCountry = String(profile.country ?? '').trim();
        const countryCurrency = getCurrencyForCountry(profileCountry);
        const profileCurrency = normalizeCurrencyCode(profile.currency ?? '');
        const nextCurrency = countryCurrency || profileCurrency || nextCurrencyOptions[0]?.value || DEFAULT_COUNTRY_CURRENCY;
        const practiceNameOnInvoice = String(profile.practiceName ?? '').trim();

        setProfileCountryLabel(getCountryDisplayName(profileCountry));
        setIsCurrencyDerivedFromCountry(Boolean(countryCurrency));
        setFormState((prev) => ({
          ...prev,
          currency: nextCurrency,
          practiceNameOnInvoice,
          paymentDue: normalizePaymentDueValue(invoiceSettings.paymentDueDays),
          showLogoOnInvoice: Boolean(invoiceSettings.showLogoOnInvoice ?? true),
          reminderBeforeDueEnabled: Boolean(invoiceSettings.reminderBeforeDueEnabled ?? true),
          reminderBeforeDue: String(readNumber(invoiceSettings.reminderBeforeDueDays, 2)),
          overdueReminderEnabled: Boolean(invoiceSettings.overdueReminderEnabled ?? true),
          vatRate: String(readNumber(invoiceSettings.vatRate, 0)),
        }));

        if (countryCurrency && profileCurrency !== countryCurrency) {
          void updateNutritionistInvoiceProfile({
            currency: countryCurrency,
            ...(practiceNameOnInvoice ? { practiceName: practiceNameOnInvoice } : {}),
          })
            .then(() => {
              updateStoredUserProfile({
                currency: countryCurrency,
                ...(practiceNameOnInvoice ? { practiceName: practiceNameOnInvoice } : {}),
              });
            })
            .catch((error) => {
              console.error('Failed to sync invoice currency from profile country:', error);
            });
        } else {
          updateStoredUserProfile({
            currency: nextCurrency,
            ...(practiceNameOnInvoice ? { practiceName: practiceNameOnInvoice } : {}),
          });
        }
      } catch (error) {
        if (isMounted) {
          showErrorToast(error, 'Unable to load invoice settings');
        }
      } finally {
        if (isMounted) setIsLoadingSettings(false);
      }
    }

    void fetchInvoiceDetails();

    return () => {
      isMounted = false;
    };
  }, []);

  const invoiceSettingsPayload = useMemo<InvoiceSettingsPayload>(() => {
    const vatRate = Math.max(0, Number(formState.vatRate) || 0);

    const payload: InvoiceSettingsPayload = {
      paymentDueDays: Number(normalizePaymentDueValue(formState.paymentDue)),
      showLogoOnInvoice: formState.showLogoOnInvoice,
      reminderBeforeDueEnabled: formState.reminderBeforeDueEnabled,
      reminderBeforeDueDays: Math.max(1, parseIntegerInput(formState.reminderBeforeDue, 2)),
      overdueReminderEnabled: formState.overdueReminderEnabled,
      chargeVat: vatRate > 0,
      vatRate,
      vatNumber: '',
    };

    return payload;
  }, [formState]);

  const currencyHelperText = useMemo(() => {
    if (isCurrencyDerivedFromCountry) {
      const countryText = profileCountryLabel || 'Practice Information country';
      return `Change the country in Practice Information to update currency.`;
    }

    return 'Set your country in Practice Information to update invoice currency.';
  }, [isCurrencyDerivedFromCountry, profileCountryLabel]);

  const handleSaveSettings = async () => {
    if (isSavingSettings) return;

    setIsSavingSettings(true);
    try {
      await Promise.all([
        updateNutritionistInvoiceSettings(invoiceSettingsPayload),
        updateNutritionistInvoiceProfile({
          currency: formState.currency,
          practiceName: formState.practiceNameOnInvoice.trim(),
        }),
      ]);
      updateStoredUserProfile({
        currency: formState.currency,
        practiceName: formState.practiceNameOnInvoice.trim(),
      });
      showSuccessToast('Invoice settings saved', 'Your invoice and tax details have been updated.');
    } catch (error) {
      showErrorToast(error, 'Unable to save invoice settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#f7f9fb',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '14px',
              border: '1px solid #dbe6eb',
              bgcolor: '#f8fafc',
              px: { xs: 2, sm: 3 },
              py: 1.25,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Settings
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Invoice and Billing
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 2.5, sm: 3.5, lg: 4 }, pb: { xs: 3, sm: 4 }, pt: { xs: 2.3, sm: 2.8 } }}>
          <Box sx={{ mb: 2.25, display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <IconButton
              aria-label="Go back"
              onClick={() => navigate(-1)}
              sx={{
                mt: 0.3,
                width: 30,
                height: 30,
                color: '#1F2B34',
                p: 0,
                '&:hover': {
                  bgcolor: 'transparent',
                },
              }}
            >
              <ArrowLeft size={28} strokeWidth={2.1} />
            </IconButton>

            <Box sx={{ minWidth: 0, pt: 0.05 }}>
              <Typography sx={{ color: '#1F2B34', fontWeight: 700, fontSize: { xs: '1.6rem', sm: '1.92rem' }, lineHeight: 1.05 }}>
                Invoice and Tax Details
              </Typography>
              <Typography sx={{ mt: 0.65, color: '#676B80', fontSize: { xs: '0.8rem', sm: '0.92rem' }, lineHeight: 1.2 }}>
                Manage invoice settings, configure your defaults, tax registration and business details.
              </Typography>
            </Box>
          </Box>

          {isLoadingSettings ? (
            <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                void handleSaveSettings();
              }}
              sx={{ display: 'grid', gap: 2.1 }}
            >
              <SettingsCard title="Default Payment Terms" subtitle="Applies to every new invoice - overrideable per invoice">
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                  columnGap: 1.75,
                  rowGap: 2.1,
                }}
              >
                <ProfileFormField
                  required
                  label="Currency"
                  value={formState.currency}
                  disabled
                  helperText={currencyHelperText}
                  sx={[invoiceFieldSx, lockedCurrencyFieldSx]}
                />
                <ProfileFormField
                  select
                  required
                  label="Payment Due In"
                  value={formState.paymentDue}
                  disabled={isSavingSettings}
                  onChange={(event) => updateField('paymentDue', event.target.value)}
                  sx={invoiceFieldSx}
                >
                  {PAYMENT_DUE_OPTIONS.map((paymentDueOption) => (
                    <MenuItem key={paymentDueOption.value} value={paymentDueOption.value}>
                      {paymentDueOption.label}
                    </MenuItem>
                  ))}
                </ProfileFormField>
              </Box>
            </SettingsCard>

            <SettingsCard title="Invoice Branding and Footer" subtitle="What appears on every invoice sent to clients">
              <Box sx={{ display: 'grid', rowGap: 2 }}>
                <ProfileFormField
                  required
                  label="Practice Name on Invoice"
                  value={formState.practiceNameOnInvoice}
                  disabled={isSavingSettings}
                  onChange={(event) => updateField('practiceNameOnInvoice', event.target.value)}
                  sx={invoiceFieldSx}
                />

                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8 }}>
                  <Switch
                    checked={formState.showLogoOnInvoice}
                    disabled={isSavingSettings}
                    onChange={(_, checked) => updateField('showLogoOnInvoice', checked)}
                    inputProps={{ 'aria-label': 'Show logo on invoice' }}
                    sx={toggleSwitchSx}
                  />
                  <Typography sx={{ color: '#6D7B87', fontSize: '0.82rem' }}>Show Logo on Invoice</Typography>
                </Box>
              </Box>
            </SettingsCard>

            {/* <SettingsCard title="Automatic Payment Reminders" subtitle="Sent to clients with outstanding invoices">
              <Box sx={{ display: 'grid', rowGap: 2.2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Typography sx={{ color: '#111A22', fontWeight: 700, fontSize: '0.98rem' }}>Reminder before due date</Typography>

                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      select
                      value={formState.reminderBeforeDue}
                      disabled={isSavingSettings || !formState.reminderBeforeDueEnabled}
                      onChange={(event) => updateField('reminderBeforeDue', event.target.value)}
                      sx={compactSelectSx}
                    >
                      {REMINDER_BEFORE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Switch
                      checked={formState.reminderBeforeDueEnabled}
                      disabled={isSavingSettings}
                      onChange={(_, checked) => updateField('reminderBeforeDueEnabled', checked)}
                      inputProps={{ 'aria-label': 'Reminder before due date' }}
                      sx={toggleSwitchSx}
                    />
                  </Box>
                </Box>

                <Box sx={{ borderTop: '1px solid #e8eef3' }} />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Typography sx={{ color: '#111A22', fontWeight: 700, fontSize: '0.98rem' }}>Overdue reminder</Typography>
                  <Switch
                    checked={formState.overdueReminderEnabled}
                    disabled={isSavingSettings}
                    onChange={(_, checked) => updateField('overdueReminderEnabled', checked)}
                    inputProps={{ 'aria-label': 'Overdue reminder' }}
                    sx={toggleSwitchSx}
                  />
                </Box>
              </Box>
            </SettingsCard> */}

            <SettingsCard title="TAX" subtitle="Tax applied, if any. Applies to your services provided.">
              <Box sx={{ display: 'grid', rowGap: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    columnGap: 1.75,
                    rowGap: 2.1,
                  }}
                >
                  <ProfileFormField
                    type="number"
                    label="TAX Rate (%)"
                    value={formState.vatRate}
                    disabled={isSavingSettings}
                    onChange={(event) => updateField('vatRate', event.target.value)}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    sx={invoiceFieldSx}
                  />
                  {/* Tax Number field is intentionally hidden because it is no longer needed. */}
                </Box>
              </Box>
            </SettingsCard>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                type="submit"
                disabled={isSavingSettings}
                sx={{
                  borderRadius: '999px',
                  px: 3,
                  py: 1.1,
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: '#13B8A6',
                  '&:hover': { bgcolor: '#0fa896' },
                }}
              >
                {isSavingSettings ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default InvoiceTaxDetailsView;
